import React, { useState } from "react";
import { motion } from "framer-motion";
import Icon from "./AppIcon";
import apiUtils from "utils/apiutils";

const ActiveBotsTable = ({ bots, isLoading, pageNo = 1, pageSize = 10, strategyName, fetchBots, fetchTotalPages }) => {
  const [editingBotId, setEditingBotId] = useState(null);
  const [editData, setEditData] = useState({});
  const [popover, setPopover] = useState({ botId: null, action: null });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBotId, setSelectedBotId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);



  const headers = [
    { key: "serial", label: "S.No" },
    { key: "Created_At", label: "Created" },
    { key: "Bot_ID", label: "Bot ID" },
    { key: "Bot_Name", label: "Name" },
    { key: "Symbol", label: "Symbol" },
    { key: "Trade_Type", label: "Trade Type" },
    { key: "Mode", label: "Mode" },
    { key: "Leverage", label: "Lev." },
    { key: "Investment_Long", label: "Inv. Long" },
    { key: "Investment_Short", label: "Inv. Short" },
    { key: "Take_Profit_Long", label: "TP Long (%)" },
    { key: "Take_Profit_Short", label: "TP Short (%)" },
    { key: "Stop_Loss_Long", label: "SL Long (%)" },
    { key: "Stop_Loss_Short", label: "SL Short (%)" },

    // { key: "Position", label: "Position" },
    { key: "Status", label: "Status" },
    // ...(strategyName === "LIQUIDATION"
    //   ? [
    //     { key: "Liquidation_Price", label: "Liq. Price" },
    //     { key: "Take_Profit_Price", label: "TP Price" },
    //     { key: "Force_Close", label: "Force Close" },
    //   ]
    //   : []),
    { key: "action", label: "Action" },
  ];

  const formatPosition = (bot) => {
    const longStatus = bot.Long_Open_Position ? "Open" : "Closed";
    const shortStatus = bot.Short_Open_Position ? "Open" : "Closed";
    return (
      <span className="block lg:flex items-center gap-1.5">
        <span
          className={`px-2 py-0.5 rounded-full flex items-center gap-1 text-xs font-medium ${longStatus === "Open"
            ? "bg-green-500/20 text-green-400"
            : "bg-red-500/20 text-red-400"
            }`}
        >
          L: <span>{longStatus}</span>
        </span>
        {bot.Mode === "HEDGE" && (
          <span
            className={`px-2 py-0.5 rounded-full flex items-center gap-1 text-xs font-medium ${shortStatus === "Open"
              ? "bg-green-500/20 text-green-400"
              : "bg-red-500/20 text-red-400"
              }`}
          >
            S: <span>{shortStatus}</span>
          </span>
        )}
      </span>
    );
  };

  const formatMode = (mode) => {
    const styles = {
      LONG: "bg-blue-500/20 text-blue-400",
      SHORT: "bg-orange-500/20 text-orange-400",
      HEDGE: "bg-purple-500/20 text-purple-400",
    };
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[mode] || "bg-muted text-foreground"
          }`}
      >
        {mode}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const handleEdit = (bot) => {
    setEditingBotId(bot.Bot_ID);
    setEditData({
      Bot_Name: bot.Bot_Name,
      Symbol: bot.Symbol,
      Mode: bot.Mode,
      Leverage: bot.Leverage,
      Investment_Long: bot.Investment_Long,
      Investment_Short: bot.Investment_Short,
      Take_Profit_Long: bot.Take_Profit_Long,
      Stop_Loss_Long: bot.Stop_Loss_Long,
      ...(strategyName === "LIQUIDATION" && {
        Liquidation_Price: bot.Liquidation_Price,
        Take_Profit_Price: bot.Take_Profit_Price,
        Force_Close: bot.Force_Close,
      }),
    });
  };

  const handleSaveEdit = async (botId) => {
    try {
      const payload = {
        strategy_name: strategyName,
        Bot_ID: botId,
        Update_Data: {
          Bot_Name: editData.Bot_Name,
          Symbol: editData.Symbol,
          Mode: editData.Mode,
          Leverage: Number(editData.Leverage),
          Investment_Long: Number(editData.Investment_Long),
          Investment_Short: Number(editData.Investment_Short),
          Take_Profit_Long: Number(editData.Take_Profit_Long),
          Stop_Loss_Long: Number(editData.Stop_Loss_Long),
          ...(strategyName === "LIQUIDATION" && {
            Liquidation_Price: editData.Liquidation_Price,
            Take_Profit_Price: editData.Take_Profit_Price,
            Force_Close: editData.Force_Close,
          }),
        },
      };
      await apiUtils.post("/bot/update_bot_data", payload, { showToast: true });
      setEditingBotId(null);
      setEditData({});
      await Promise.all([fetchBots(), fetchTotalPages()]);
    } catch (e) {
      console.error("Error updating bot:", e);
    }
  };

  const handleAction = async (botId, action) => {
    const endpointMap = {
      start: "/bot/start_bot",
      stop: "/bot/stop_bot",
      delete: "/bot/delete_bot",
    };
    try {
      const payload = {
        Bot_ID: botId,
        strategy_name: strategyName,
        close_all_positions: confirmDelete
      };
      await apiUtils.post(endpointMap[action], payload, { showToast: true });
      setPopover({ botId: null, action: null });
      await Promise.all([fetchBots(), fetchTotalPages()]);
    } catch (e) {
      console.error(`Error performing ${action}:`, e);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Icon name="Loader" size={28} className="animate-spin text-ring" />
      </div>
    );
  }

  if (bots.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Icon name="Inbox" size={48} className="mx-auto mb-4 opacity-30" />
        <p className="text-lg">No active bots found for this strategy.</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto scrollbar-thin-custom mt-5">
        <table className="w-full text-left">
          <thead className="text-sm uppercase bg-card/50 border-b border-border">
            <tr>
              {headers.map((h) => (
                <th
                  key={h.key}
                  className="px-3 py-3 font-medium text-foreground/70 whitespace-nowrap"
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bots?.map((bot, index) => (
              <motion.tr
                key={bot.Bot_ID}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: index * 0.03 }}
                className="border-b border-border hover:bg-primary/5 transition text-xs"
              >
                <td className="px-3 py-3 font-medium text-foreground/80">
                  {(pageNo - 1) * pageSize + index + 1}
                </td>
                <td className="px-3 py-3 text-muted-foreground">
                  {formatDate(bot.Created_At)}
                </td>
                <td className="px-3 py-3 font-medium">{bot.Bot_ID || "-"}</td>
                <td className="px-3 py-3">
                  {editingBotId === bot.Bot_ID ? (
                    <input
                      type="text"
                      value={editData.Bot_Name || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Bot_Name: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  ) : (
                    bot.Bot_Name || "-"
                  )}
                </td>
                <td className="px-3 py-3 font-medium">
                  {editingBotId === bot.Bot_ID ? (
                    <input
                      type="text"
                      value={editData.Symbol || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Symbol: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  ) : (
                    bot.Symbol || "-"
                  )}
                </td>
                <td className="px-3 py-3 font-medium">{bot.Trade_Type || "-"}</td>
                <td className="px-3 py-3">
                  {editingBotId === bot.Bot_ID ? (
                    <select
                      value={editData.Mode || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Mode: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    >
                      <option value="LONG">LONG</option>
                      <option value="SHORT">SHORT</option>
                      <option value="HEDGE">HEDGE</option>
                    </select>
                  ) : (
                    formatMode(bot.Mode)
                  )}
                </td>
                <td className="px-3 py-3">
                  {editingBotId === bot.Bot_ID ? (
                    <input
                      type="number"
                      value={editData.Leverage || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Leverage: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  ) : (
                    (bot.Leverage ? `${bot.Leverage}x` : "-")
                  )}
                </td>
                <td className="px-3 py-3">
                  {editingBotId === bot.Bot_ID ? (
                    <input
                      type="number"
                      value={editData.Investment_Long || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Investment_Long: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  ) : (
                    bot.Investment_Long ?? "-"
                  )}
                </td>
                <td className="px-3 py-3">
                  {editingBotId === bot.Bot_ID ? (
                    <input
                      type="number"
                      value={editData.Investment_Short || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Investment_Short: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  ) : (
                    bot.Investment_Short ?? "-"
                  )}
                </td>
                <td className="px-3 py-3">
                  {editingBotId === bot.Bot_ID ? (
                    <input
                      type="number"
                      value={editData.Take_Profit_Long || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Take_Profit_Long: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  ) : (
                    bot.Take_Profit_Long ?? "-"
                  )}
                </td>

                <td className="px-3 py-3">
                  {editingBotId === bot.Bot_ID ? (
                    <input
                      type="number"
                      value={editData.Take_Profit_Short || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Take_Profit_Short: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  ) : (
                    bot.Take_Profit_Short ?? "-"
                  )}
                </td>



                <td className="px-3 py-3">
                  {editingBotId === bot.Bot_ID ? (
                    <input
                      type="number"
                      value={editData.Stop_Loss_Long || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Stop_Loss_Long: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  ) : (
                    bot.Stop_Loss_Long ?? "-"
                  )}
                </td>
                <td className="px-3 py-3">
                  {editingBotId === bot.Bot_ID ? (
                    <input
                      type="number"
                      value={editData.Stop_Loss_Short || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Stop_Loss_Short: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  ) : (
                    bot.Stop_Loss_Short ?? "-"
                  )}
                </td>
                {/* <td className="px-3 py-3">{formatPosition(bot)}</td> */}
                <td className="px-3 py-3">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${bot.Status === "ACTIVE"
                      ? "bg-green-500/20 text-green-400"
                      : "bg-gray-500/20 text-gray-400"
                      }`}
                  >
                    {bot.Status || "-"}
                  </span>
                </td>
                {/* {strategyName === "LIQUIDATION" && (
                  <>
                    <td className="px-3 py-3">
                      {editingBotId === bot.Bot_ID ? (
                        <input
                          type="number"
                          value={editData.Liquidation_Price || ""}
                          onChange={(e) =>
                            setEditData({ ...editData, Liquidation_Price: e.target.value })
                          }
                          className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                        />
                      ) : (
                        bot.Liquidation_Price ?? "-"
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {editingBotId === bot.Bot_ID ? (
                        <input
                          type="number"
                          value={editData.Take_Profit_Price || ""}
                          onChange={(e) =>
                            setEditData({ ...editData, Take_Profit_Price: e.target.value })
                          }
                          className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                        />
                      ) : (
                        bot.Take_Profit_Price ?? "-"
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {editingBotId === bot.Bot_ID ? (
                        <select
                          value={editData.Force_Close || false}
                          onChange={(e) =>
                            setEditData({ ...editData, Force_Close: e.target.value === "true" })
                          }
                          className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                        >
                          <option value={true}>True</option>
                          <option value={false}>False</option>
                        </select>
                      ) : (
                        bot.Force_Close ? "True" : "False"
                      )}
                    </td>
                  </>
                )} */}
                <td className="px-3 py-3 relative">
                  {editingBotId === bot.Bot_ID ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveEdit(bot.Bot_ID)}
                        className="p-1 text-green-500 hover:text-green-600"
                      >
                        <Icon name="Check" size={16} />
                      </button>
                      <button
                        onClick={() => setEditingBotId(null)}
                        className="p-1 text-red-500 hover:text-red-600"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2 relative">
                      {/* <button
                        onClick={() => handleEdit(bot)}
                        className="p-1 text-blue-500 hover:text-blue-600"
                      >
                        <Icon name="Edit" size={16} />
                      </button> */}
                      {bot.Status === "ACTIVE" ? (
                        <button
                          onClick={() => setPopover({ botId: bot.Bot_ID, action: "stop" })}
                          className="p-1 text-yellow-500 hover:text-yellow-600"
                        >
                          <Icon name="Pause" size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => setPopover({ botId: bot.Bot_ID, action: "start" })}
                          className="p-1 text-green-500 hover:text-green-600"
                        >
                          <Icon name="Play" size={16} />
                        </button>
                      )}
                      {/* <button
                        onClick={() => setPopover({ botId: bot.Bot_ID, action: "start" })}
                        className="p-1 text-green-500 hover:text-green-600"
                        disabled={bot.Status === "ACTIVE"}
                      >
                        <Icon name="Play" size={16} />
                      </button>
                      <button
                        onClick={() => setPopover({ botId: bot.Bot_ID, action: "stop" })}
                        className="p-1 text-yellow-500 hover:text-yellow-600"
                        disabled={bot.Status !== "ACTIVE"}
                      >
                        <Icon name="Pause" size={16} />
                      </button> */}
                      <button
                        onClick={() => {
                          setSelectedBotId(bot.Bot_ID);
                          setShowDeleteModal(true);
                          setConfirmDelete(false);
                        }}
                        className="p-1 text-red-500 hover:text-red-600"
                      >
                        <Icon name="Trash" size={16} />
                      </button>
                      {popover.botId === bot.Bot_ID && popover.action && (
                        <div className="absolute z-10 bg-card border border-border rounded p-3 shadow-lg -top-32 ">
                          <p className="text-sm text-foreground mb-2">
                            Are you sure you want to {popover.action} this bot?
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleAction(bot.Bot_ID, popover.action)}
                              className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs"
                            >
                              Okay
                            </button>
                            <button
                              onClick={() => setPopover({ botId: null, action: null })}
                              className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}


                    </div>
                  )}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>



      </div>

      {/* Mobile Cards */}
      <div className="md:hidden p-4 space-y-4">
        {bots?.map((bot, index) => (
          <motion.div
            key={bot.Bot_ID}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: index * 0.05 }}
            className="bg-card/50 border border-border rounded-xl p-4 shadow-sm relative"
          >
            <div className="text-xs text-muted-foreground mb-3">
              Bot #{(pageNo - 1) * pageSize + index + 1} • {formatDate(bot.Created_At)}
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-foreground/70">Bot ID</span>
                <p className="font-medium truncate">{bot.Bot_ID || "-"}</p>
              </div>
              <div>
                <span className="text-foreground/70">Name</span>
                {editingBotId === bot.Bot_ID ? (
                  <input
                    type="text"
                    value={editData.Bot_Name || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, Bot_Name: e.target.value })
                    }
                    className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                  />
                ) : (
                  <p className="font-medium">{bot.Bot_Name || "-"}</p>
                )}
              </div>
              <div>
                <span className="text-foreground/70">Symbol</span>
                {editingBotId === bot.Bot_ID ? (
                  <input
                    type="text"
                    value={editData.Symbol || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, Symbol: e.target.value })
                    }
                    className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                  />
                ) : (
                  <p className="font-medium">{bot.Symbol || "-"}</p>
                )}
              </div>

              <div>
                <span className="text-foreground/70">Trade Type</span>
                <p>{bot.Trade_Type || "-"}</p>
              </div>
              <div>
                <span className="text-foreground/70">Mode</span>
                {editingBotId === bot.Bot_ID ? (
                  <select
                    value={editData.Mode || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, Mode: e.target.value })
                    }
                    className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                  >
                    <option value="LONG">LONG</option>
                    <option value="SHORT">SHORT</option>
                    <option value="HEDGE">HEDGE</option>
                  </select>
                ) : (
                  <p>{formatMode(bot.Mode)}</p>
                )}
              </div>
              <div>
                <span className="text-foreground/70">Leverage</span>
                {editingBotId === bot.Bot_ID ? (
                  <input
                    type="number"
                    value={editData.Leverage || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, Leverage: e.target.value })
                    }
                    className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                  />
                ) : (
                  <p>{bot.Leverage ? `${bot.Leverage}x` : "-"}</p>
                )}
              </div>
              <div>
                <span className="text-foreground/70">Inv. Long/Short</span>
                {editingBotId === bot.Bot_ID ? (
                  <div className="flex gap-1">
                    <input
                      type="number"
                      value={editData.Investment_Long || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Investment_Long: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                    <input
                      type="number"
                      value={editData.Investment_Short || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Investment_Short: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  </div>
                ) : (
                  <p>
                    {bot.Investment_Long ?? "-"} / {bot.Investment_Short ?? "-"}
                  </p>
                )}
              </div>
              {/* // */}
              <div>
                <span className="text-foreground/70">TP/SL Long</span>
                {editingBotId === bot.Bot_ID ? (
                  <div className="flex gap-1">
                    <input
                      type="number"
                      value={editData.Take_Profit_Long || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Take_Profit_Long: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                    <input
                      type="number"
                      value={editData.Stop_Loss_Long || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Stop_Loss_Long: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  </div>
                ) : (
                  <p>
                    {bot.Take_Profit_Long ?? "-"}% / {bot.Stop_Loss_Long ?? "-"}%
                  </p>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-foreground/70">TP/SL Short</span>

                {editingBotId === bot.Bot_ID ? (
                  <div className="flex gap-1">
                    <input
                      type="number"
                      value={editData.Take_Profit_Short || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, Take_Profit_Short: e.target.value })
                      }
                      className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                    />
                  </div>
                ) : (

                  <p>
                    {bot.Take_Profit_Short ?? "-"}% / {bot.Stop_Loss_Short ?? "-"}%
                  </p>
                )}
              </div>


              {/* // */}
              {/* <div>
                <span className="text-foreground/70">Position</span>
                <p>{formatPosition(bot)}</p>
              </div>
              {strategyName === "LIQUIDATION" && (
                <>
                  <div>
                    <span className="text-foreground/70">Liq. Price</span>
                    {editingBotId === bot.Bot_ID ? (
                      <input
                        type="number"
                        value={editData.Liquidation_Price || ""}
                        onChange={(e) =>
                          setEditData({ ...editData, Liquidation_Price: e.target.value })
                        }
                        className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                      />
                    ) : (
                      <p>{bot.Liquidation_Price ?? "-"}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-foreground/70">TP Price</span>
                    {editingBotId === bot.Bot_ID ? (
                      <input
                        type="number"
                        value={editData.Take_Profit_Price || ""}
                        onChange={(e) =>
                          setEditData({ ...editData, Take_Profit_Price: e.target.value })
                        }
                        className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                      />
                    ) : (
                      <p>{bot.Take_Profit_Price ?? "-"}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-foreground/70">Force Close</span>
                    {editingBotId === bot.Bot_ID ? (
                      <select
                        value={editData.Force_Close || false}
                        onChange={(e) =>
                          setEditData({ ...editData, Force_Close: e.target.value === "true" })
                        }
                        className="w-full px-2 py-1 text-xs bg-card border border-border rounded focus:border-primary focus:outline-none"
                      >
                        <option value={true}>True</option>
                        <option value={false}>False</option>
                      </select>
                    ) : (
                      <p>{bot.Force_Close ? "True" : "False"}</p>
                    )}
                  </div>
                </>
              )} */}
              <div className="col-span-2">
                <span className="text-foreground/70">Status</span>
                <p>
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${bot.Status === "ACTIVE"
                      ? "bg-green-500/20 text-green-400"
                      : "bg-gray-500/20 text-gray-400"
                      }`}
                  >
                    {bot.Status || "-"}
                  </span>
                </p>
              </div>
              <div className="col-span-2 relative">
                <span className="text-foreground/70">Actions</span>
                <div className="flex gap-2 mt-1">
                  {editingBotId === bot.Bot_ID ? (
                    <>
                      <button
                        onClick={() => handleSaveEdit(bot.Bot_ID)}
                        className="p-1 text-green-500 hover:text-green-600"
                      >
                        <Icon name="Check" size={16} />
                      </button>
                      <button
                        onClick={() => setEditingBotId(null)}
                        className="p-1 text-red-500 hover:text-red-600"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      {/* <button
                        onClick={() => handleEdit(bot)}
                        className="p-1 text-blue-500 hover:text-blue-600"
                      >
                        <Icon name="Edit" size={16} />
                      </button> */}
                      {bot.Status === "ACTIVE" ? (
                        <button
                          onClick={() => setPopover({ botId: bot.Bot_ID, action: "stop" })}
                          className="p-1 text-yellow-500 hover:text-yellow-600"
                        >
                          <Icon name="Pause" size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => setPopover({ botId: bot.Bot_ID, action: "start" })}
                          className="p-1 text-green-500 hover:text-green-600"
                        >
                          <Icon name="Play" size={16} />
                        </button>
                      )}
                      {/* <button
                        onClick={() => setPopover({ botId: bot.Bot_ID, action: "start" })}
                        className="p-1 text-green-500 hover:text-green-600"
                        disabled={bot.Status === "ACTIVE"}
                      >
                        <Icon name="Play" size={16} />
                      </button>
                      <button
                        onClick={() => setPopover({ botId: bot.Bot_ID, action: "stop" })}
                        className="p-1 text-yellow-500 hover:text-yellow-600"
                        disabled={bot.Status !== "ACTIVE"}
                      >
                        <Icon name="Pause" size={16} />
                      </button> */}
                      <button
                        onClick={() => {
                          setSelectedBotId(bot.Bot_ID);
                          setShowDeleteModal(true);
                          setConfirmDelete(false);
                        }}
                        className="p-1 text-red-500 hover:text-red-600"
                      >
                        <Icon name="Trash" size={16} />
                      </button>
                    </>
                  )}
                </div>
                {popover.botId === bot.Bot_ID && popover.action && (
                  <div className="absolute z-10 bg-card border border-border rounded p-3 shadow-lg -top-12 !left-0 right-5">
                    <p className="text-sm text-foreground mb-2">
                      Are you sure you want to {popover.action} this bot?
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAction(bot.Bot_ID, popover.action)}
                        className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs"
                      >
                        Okay
                      </button>
                      <button
                        onClick={() => setPopover({ botId: null, action: null })}
                        className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {showDeleteModal && (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
    <div className="bg-card rounded-2xl p-6 w-full max-w-sm border border-border shadow-2xl animate-in fade-in zoom-in duration-200">
      
      {/* Header */}
      <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
        <Icon name="AlertTriangle" size={20} className="text-red-500" />
        Delete Bot
      </h2>

      {/* Description */}
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
        Are you sure you want to delete this bot? This action will permanently remove the bot from your list.
      </p>

      {/* Checkbox */}
      <label className="flex items-start gap-3 mt-4 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={confirmDelete}
          onChange={(e) => setConfirmDelete(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-red-500 rounded"
        />
        <span className="text-foreground/80">
          This action will <span className="uppercase font-semibold text-red-500"> close all positions</span> and <strong>cannot be undone</strong>.
        </span>
      </label>

      {/* Footer Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        
        {/* Cancel Button */}
        <button
          onClick={() => setShowDeleteModal(false)}
          className="px-4 py-1.5 rounded-lg bg-muted hover:bg-muted/70 text-muted-foreground text-sm transition"
        >
          Cancel
        </button>

        {/* Delete Button */}
        <button
          disabled={!confirmDelete}
          onClick={async () => {
            await handleAction(selectedBotId, "delete");
            setShowDeleteModal(false);
            setConfirmDelete(false); // reset checkbox
          }}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
            ${confirmDelete
              ? "bg-red-600 hover:bg-red-700 text-white"
              : "bg-red-500/40 text-white/60 cursor-not-allowed"
            }`}
        >
          Delete Bot
        </button>
      </div>
    </div>
  </div>
)}

    </>
  );
};

export default ActiveBotsTable;